import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getAutomationConfig, updateAutomationConfig, createBatch, getBatches, getBatchDetail, controlBatch, deleteBatch } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Robot, Play, Pause, Stop, Gear, Trash, Eye, Lightning, CheckCircle, XCircle, Clock, ArrowClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';

const PROVIDERS = { indane: 'Indane Gas', bpcl: 'Bharat Gas', hp: 'HP Gas' };
const STATUS_COLORS = {
  ready: 'bg-blue-100 text-blue-700', running: 'bg-amber-100 text-amber-700 animate-pulse',
  paused: 'bg-yellow-100 text-yellow-700', completed: 'bg-green-100 text-green-700',
  stopped: 'bg-gray-100 text-gray-600',
};
const TASK_COLORS = {
  pending: 'bg-gray-100 text-gray-500', running: 'bg-blue-100 text-blue-700',
  login: 'bg-indigo-100 text-indigo-700', topup: 'bg-amber-100 text-amber-700',
  bill_fetch: 'bg-orange-100 text-orange-700', payment: 'bg-purple-100 text-purple-700',
  done: 'bg-green-100 text-green-700', failed: 'bg-red-100 text-red-700',
};

export default function AutomationPage() {
  const { user } = useAuth();
  const [batches, setBatches] = useState([]);
  const [config, setConfig] = useState({ captcha_api_key: '', card_pin: '', parallel_browsers: 2, delay_between_bookings: 5, headless: true });
  const [configOpen, setConfigOpen] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchDetail, setBatchDetail] = useState(null);
  const [newBatchProvider, setNewBatchProvider] = useState('indane');
  const [newBatchName, setNewBatchName] = useState('');
  const [creating, setCreating] = useState(false);
  const pollRef = useRef(null);

  const fetchBatches = useCallback(async () => {
    try { const res = await getBatches(); setBatches(res.data); } catch (err) { console.error("Load failed:", err?.message); }
  }, []);

  useEffect(() => { fetchBatches(); }, [fetchBatches]);

  // Auto-refresh running batches
  useEffect(() => {
    const hasRunning = batches.some(b => b.status === 'running');
    if (hasRunning) {
      pollRef.current = setInterval(async () => {
        try {
          const res = await getBatches(); setBatches(res.data);
          if (batchDetail && detailOpen) {
            const dr = await getBatchDetail(batchDetail.id); setBatchDetail(dr.data);
          }
        } catch (err) { console.error("Load failed:", err?.message); }
      }, 5000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [batches.length, batchDetail, detailOpen]);

  const loadConfig = async () => {
    try { const res = await getAutomationConfig(); setConfig(res.data); } catch (err) { console.error("Load failed:", err?.message); }
  };
  const saveConfig = async () => {
    setConfigSaving(true);
    try { await updateAutomationConfig(config); toast.success('Config saved'); setConfigOpen(false); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setConfigSaving(false); }
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await createBatch({ name: newBatchName, provider: newBatchProvider, auto_assign: true });
      toast.success(res.data.message);
      setNewBatchName(''); fetchBatches();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setCreating(false); }
  };

  const handleControl = async (id, action) => {
    try { await controlBatch(id, action); toast.success(`Batch ${action}`); fetchBatches(); }
    catch (err) { toast.error('Failed'); }
  };

  const openDetail = async (b) => {
    setSelectedBatch(b);
    try { const res = await getBatchDetail(b.id); setBatchDetail(res.data); setDetailOpen(true); }
    catch (err) { toast.error('Failed to load'); }
  };

  const formatCurrency = (v) => v ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v) : '—';

  if (user?.role !== 'admin' && user?.role !== 'owner') return <div className="p-6 text-center">Admin Only</div>;

  return (
    <div className="space-y-5" data-testid="automation-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Robot size={24} weight="fill" className="text-violet-600" /> LPG Automation</h1>
          <p className="text-xs text-gray-500">Batch booking automation — Cloud server se fully automatic</p>
        </div>
        <Button variant="outline" size="sm" className="gap-1.5 h-8 text-xs self-start" onClick={() => { loadConfig(); setConfigOpen(true); }} data-testid="auto-config-btn">
          <Gear size={14} /> Config
        </Button>
      </div>

      {/* Create Batch */}
      <div className="bg-white border rounded-lg p-4">
        <h2 className="text-sm font-bold mb-3">Create New Batch</h2>
        <div className="flex flex-col sm:flex-row gap-3 items-end">
          <div className="flex-1">
            <Label className="text-xs">Batch Name</Label>
            <Input value={newBatchName} onChange={e => setNewBatchName(e.target.value)} placeholder="e.g. Morning Batch" className="mt-1" />
          </div>
          <div className="w-40">
            <Label className="text-xs">Provider</Label>
            <Select value={newBatchProvider} onValueChange={setNewBatchProvider}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(PROVIDERS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button className="bg-violet-600 hover:bg-violet-700 gap-1.5" onClick={handleCreate} disabled={creating} data-testid="create-batch-btn">
            {creating ? <><div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> Creating...</> : <><Lightning size={16} weight="fill" /> Create Batch</>}
          </Button>
        </div>
        <p className="text-[10px] text-gray-400 mt-2">Auto-assign: LPG IDs + Amazon Accounts + Gift Cards → Ek click mein batch ready</p>
      </div>

      {/* Batches List */}
      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
          <h2 className="text-sm font-bold">Batches</h2>
          <Button size="sm" variant="ghost" className="text-xs gap-1" onClick={fetchBatches}><ArrowClockwise size={12} /> Refresh</Button>
        </div>
        {batches.length === 0 ? (
          <p className="text-center py-8 text-gray-400 text-sm">No batches created yet</p>
        ) : (
          <div className="divide-y">
            {batches.map(b => {
              const pct = b.total_tasks ? Math.round(((b.completed + b.failed) / b.total_tasks) * 100) : 0;
              return (
                <div key={b.id} className="p-3 sm:p-4" data-testid={`batch-${b.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-sm truncate">{b.name}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${STATUS_COLORS[b.status] || ''}`}>{b.status.toUpperCase()}</span>
                        <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{PROVIDERS[b.provider]}</span>
                      </div>
                      <div className="flex gap-4 text-xs text-gray-500">
                        <span>Total: <b>{b.total_tasks}</b></span>
                        <span className="text-green-600">Done: <b>{b.completed}</b></span>
                        <span className="text-red-600">Failed: <b>{b.failed}</b></span>
                        {b.running > 0 && <span className="text-blue-600">Running: <b>{b.running}</b></span>}
                      </div>
                      {/* Progress bar */}
                      <div className="mt-2 bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div className="h-full rounded-full flex">
                          <div className="bg-green-500 transition-all" style={{ width: `${b.total_tasks ? (b.completed / b.total_tasks * 100) : 0}%` }} />
                          <div className="bg-red-400 transition-all" style={{ width: `${b.total_tasks ? (b.failed / b.total_tasks * 100) : 0}%` }} />
                          <div className="bg-blue-400 animate-pulse transition-all" style={{ width: `${b.total_tasks ? (b.running / b.total_tasks * 100) : 0}%` }} />
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => openDetail(b)}><Eye size={14} /> View</Button>
                      {b.status === 'ready' && <Button size="sm" className="bg-green-600 hover:bg-green-700 h-8 text-xs gap-1" onClick={() => handleControl(b.id, 'start')}><Play size={14} weight="fill" /> Start</Button>}
                      {b.status === 'running' && <Button size="sm" className="bg-amber-500 hover:bg-amber-600 h-8 text-xs gap-1" onClick={() => handleControl(b.id, 'pause')}><Pause size={14} weight="fill" /> Pause</Button>}
                      {b.status === 'paused' && <Button size="sm" className="bg-green-600 hover:bg-green-700 h-8 text-xs gap-1" onClick={() => handleControl(b.id, 'start')}><Play size={14} weight="fill" /> Resume</Button>}
                      {['running', 'paused'].includes(b.status) && <Button size="sm" variant="outline" className="text-red-600 h-8 text-xs gap-1" onClick={() => handleControl(b.id, 'stop')}><Stop size={14} weight="fill" /></Button>}
                      {['completed', 'stopped', 'ready'].includes(b.status) && <Button size="sm" variant="ghost" className="text-red-400 h-8" onClick={() => { deleteBatch(b.id).then(fetchBatches); }}><Trash size={14} /></Button>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Cloud Server Setup Guide */}
      <div className="bg-violet-50 border border-violet-200 rounded-lg p-4">
        <h3 className="font-bold text-sm text-violet-800 mb-2">Cloud Server Setup</h3>
        <div className="text-xs text-violet-700 space-y-1">
          <p>1. <b>Ubuntu VPS</b> le lo (DigitalOcean/AWS/Hetzner) — 4GB RAM minimum</p>
          <p>2. Server pe run karo:</p>
          <pre className="bg-violet-100 p-2 rounded font-mono text-[10px] mt-1 overflow-x-auto">
{`sudo apt update && sudo apt install -y python3 python3-pip
pip3 install playwright 2captcha-python pyotp requests
playwright install chromium --with-deps
# Download worker script from web app
# python3 lpg_worker.py`}
          </pre>
          <p>3. <b>Worker script</b> download karo Settings se → cloud server pe run karo</p>
          <p>4. Web app se <b>Batch create + Start</b> karo → Worker auto pickup karega</p>
        </div>
      </div>

      {/* Batch Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-3xl max-h-[85vh] overflow-y-auto">
          {batchDetail && (
            <>
              <DialogHeader>
                <DialogTitle>{batchDetail.name} — {PROVIDERS[batchDetail.provider]}</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                <div className="bg-gray-50 p-2 rounded text-center"><p className="text-[10px] text-gray-400">Total</p><p className="text-lg font-bold">{batchDetail.total_tasks}</p></div>
                <div className="bg-green-50 p-2 rounded text-center"><p className="text-[10px] text-green-600">Done</p><p className="text-lg font-bold text-green-700">{batchDetail.completed}</p></div>
                <div className="bg-red-50 p-2 rounded text-center"><p className="text-[10px] text-red-600">Failed</p><p className="text-lg font-bold text-red-700">{batchDetail.failed}</p></div>
                <div className="bg-blue-50 p-2 rounded text-center"><p className="text-[10px] text-blue-600">Pending</p><p className="text-lg font-bold text-blue-700">{batchDetail.pending}</p></div>
              </div>
              <div className="mt-3 overflow-x-auto max-h-[400px] overflow-y-auto">
                <Table>
                  <TableHeader><TableRow className="bg-gray-50 sticky top-0">
                    <TableHead className="text-xs">#</TableHead>
                    <TableHead className="text-xs">LPG ID</TableHead>
                    <TableHead className="text-xs">Account</TableHead>
                    <TableHead className="text-xs">Step</TableHead>
                    <TableHead className="text-xs">Amount</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs">Error</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {batchDetail.tasks.map((t, i) => (
                      <TableRow key={t.lpg_id || `task-${i}`} className={t.status === 'failed' ? 'bg-red-50/30' : t.status === 'done' ? 'bg-green-50/30' : ''}>
                        <TableCell className="text-xs text-gray-400">{i + 1}</TableCell>
                        <TableCell className="font-mono text-sm font-bold">{t.lpg_id}</TableCell>
                        <TableCell className="text-xs truncate max-w-[120px]">{t.account_email}</TableCell>
                        <TableCell><span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${TASK_COLORS[t.current_step || t.status] || 'bg-gray-100'}`}>{t.current_step || t.status}</span></TableCell>
                        <TableCell className="text-xs font-mono">{t.bill_amount ? formatCurrency(t.bill_amount) : '—'}</TableCell>
                        <TableCell>{t.status === 'done' ? <CheckCircle size={16} className="text-green-600" /> : t.status === 'failed' ? <XCircle size={16} className="text-red-600" /> : t.status === 'running' ? <div className="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" /> : <Clock size={14} className="text-gray-400" />}</TableCell>
                        <TableCell className="text-[10px] text-red-600 max-w-[150px] truncate">{t.error_message || ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Config Dialog */}
      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-md">
          <DialogHeader><DialogTitle>Automation Config</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs">2Captcha API Key</Label>
              <Input value={config.captcha_api_key} onChange={e => setConfig(p => ({ ...p, captcha_api_key: e.target.value }))} className="font-mono text-xs mt-1" data-testid="captcha-key" />
            </div>
            <div>
              <Label className="text-xs">Card PIN (sab cards ka same)</Label>
              <Input type="password" value={config.card_pin} onChange={e => setConfig(p => ({ ...p, card_pin: e.target.value }))} className="font-mono text-xs mt-1" data-testid="card-pin" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Parallel Browsers</Label>
                <Input type="number" min={1} max={10} value={config.parallel_browsers} onChange={e => setConfig(p => ({ ...p, parallel_browsers: parseInt(e.target.value) || 2 }))} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Delay (seconds)</Label>
                <Input type="number" min={1} max={60} value={config.delay_between_bookings} onChange={e => setConfig(p => ({ ...p, delay_between_bookings: parseInt(e.target.value) || 5 }))} className="mt-1" />
              </div>
            </div>
            <Button className="w-full bg-violet-600 hover:bg-violet-700" onClick={saveConfig} disabled={configSaving}>{configSaving ? 'Saving...' : 'Save Config'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
